import { create } from 'zustand';

/**
 * Sistema de confirm imperativo (estilo sonner). Substitui o `window.confirm`
 * nativo que tem visual feio do browser e bloqueia a thread.
 *
 * Uso:
 * ```ts
 * import { confirm } from '@/shared/lib/confirm';
 * if (await confirm({ title: 'Excluir item?', confirmText: 'Excluir', variant: 'danger' })) {
 *   deleteMut.mutate();
 * }
 * ```
 *
 * O store guarda um único dialog ativo + uma callback que é chamada quando
 * o usuário confirma/cancela. O componente <ConfirmDialog /> (montado no
 * AppShell) lê do store e renderiza.
 */

export type ConfirmVariant = 'primary' | 'danger';

export interface ConfirmOptions {
  title: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  /** 'danger' = botão vermelho (delete/destrutivo); 'primary' = azul (ação positiva). */
  variant?: ConfirmVariant;
  /**
   * Palavra que a pessoa precisa DIGITAR pra liberar o botão (ex.: 'EXCLUIR').
   *
   * Pra ação destrutiva que não tem volta. Um clique sai no reflexo — digitar
   * a palavra obriga a ler o que está sendo feito. Mesmo padrão do apagar
   * contatos em massa. Sem isto, o dialog segue só com os dois botões.
   */
  requireText?: string;
}

/**
 * O que foi digitado libera o botão?
 *
 * Ignora espaço sobrando e maiúsculas: quem digitou "excluir " claramente leu
 * e quis. O atrito que importa é ter que escrever a palavra, não acertar a
 * tecla Caps Lock.
 */
export function confirmacaoBate(digitado: string, exigido: string | undefined): boolean {
  if (!exigido) return true;
  return digitado.trim().toLowerCase() === exigido.trim().toLowerCase();
}

interface ConfirmState {
  open: boolean;
  options: ConfirmOptions | null;
  resolve: ((ok: boolean) => void) | null;
  show: (opts: ConfirmOptions) => Promise<boolean>;
  resolveAndClose: (ok: boolean) => void;
}

export const useConfirmStore = create<ConfirmState>((set, get) => ({
  open: false,
  options: null,
  resolve: null,
  show: (opts) =>
    new Promise<boolean>((resolve) => {
      // Se já tem um dialog aberto, fecha ele resolvendo como false (cancelado).
      const prev = get().resolve;
      if (prev) prev(false);
      set({ open: true, options: opts, resolve });
    }),
  resolveAndClose: (ok) => {
    const { resolve } = get();
    if (resolve) resolve(ok);
    set({ open: false, options: null, resolve: null });
  },
}));

/** API ergonômica — substitui chamadas `confirm(...)` nativas. */
export function confirm(opts: ConfirmOptions): Promise<boolean> {
  return useConfirmStore.getState().show(opts);
}
